<script setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import ImageComponent from '@/components/common/ImageComponent.vue';
import PreviewHeader from '@/components/preview/kakao/common/PreviewHeader.vue';
import PreviewFooter from '@/components/preview/kakao/common/PreviewFooter.vue';
import { COUPON_TYPE_OPTIONS } from '@/config/constant';
import addComma from '@/utils/addComma';

const { t } = useI18n();

const props = defineProps({
  position: {
    type: String,
    default: '',
  },
  friendtalkCommerce: {
    type: Object,
    default: () => ({}),
  },
  kakaoProfile: {
    type: String,
    default: '',
  },
});

const {
  friendtalkCommerce,
  position,
} = toRefs(props);

const couponType = COUPON_TYPE_OPTIONS.reduce((current, item) => ({
  [`${item.type}`]: item.text,
  ...current,
}), {});

const imageUrl = computed(() => friendtalkCommerce.value.image?.url);

const title = computed(() => friendtalkCommerce.value.title || t('kakaoBasic.preview.productName'));

const discountPrice = computed(() => {
  const {
    discountPrice: discountPriceValue,
  } = friendtalkCommerce.value;

  if (discountPriceValue) {
    return `${addComma(discountPriceValue)}`;
  }

  return t('kakaoBasic.preview.discountPrice');
});

const discountPriceSuffix = computed(() => {
  const {
    discountPrice: discountPriceValue,
  } = friendtalkCommerce.value;

  if (discountPriceValue) {
    return t('kakaoBasic.preview.KRW');
  }

  return '';
});

const regularPrice = computed(() => {
  const {
    regularPrice: regularPriceValue,
    discountPrice: discountPriceValue,
  } = friendtalkCommerce.value;

  if (regularPriceValue) {
    if (!discountPriceValue) {
      return `${addComma(regularPriceValue)}`;
    }

    return addComma(regularPriceValue);
  }

  return t('kakaoBasic.preview.regularPrice');
});

const regularPriceSuffix = computed(() => {
  const {
    regularPrice: regularPriceValue,
    discountPrice: discountPriceValue,
  } = friendtalkCommerce.value;

  if (regularPriceValue) {
    if (!discountPriceValue) {
      return t('kakaoBasic.preview.KRW');
    }

    return '';
  }

  return '';
});

const discountRate = computed(() => {
  const {
    discountRate: rateValue,
    discountFixed: fixedValue,
  } = friendtalkCommerce.value;

  if (rateValue) {
    return rateValue;
  }

  if (fixedValue) {
    return '';
  }

  return t('kakaoBasic.preview.discountRate');
});

const discountFixed = computed(() => {
  const value = friendtalkCommerce.value.discountFixed;

  if (value) {
    return `${addComma(value)}`;
  }

  return t('kakaoBasic.preview.discountFixed');
});

const discountFixedSuffix = computed(() => {
  const value = friendtalkCommerce.value.discountFixed;

  if (value) {
    return t('kakaoBasic.preview.KRW');
  }

  return '';
});

const additionalContent = computed(() => friendtalkCommerce.value.additionalContent || '');

const imageClass = computed(() => {
  const hover = position.value === 'commerce-image';

  return hover ? 'editAreaPositionHover' : '';
});

const titleClass = computed(() => {
  const hover = position.value === 'commerce-title';

  return hover ? 'editAreaPositionHover' : '';
});

const discountPriceClass = computed(() => {
  const {
    regularPrice: regularPriceValue,
    discountPrice: discountPriceValue,
  } = friendtalkCommerce.value;
  const classList = [];

  if (regularPriceValue && !discountPriceValue) {
    classList.push('d-none');
  } else {
    classList.push('price__sale');
  }

  const hover = position.value === 'commerce-discountPrice';

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  if (!friendtalkCommerce.value.discountPrice) {
    classList.push('default-value-display');
  }

  return classList.join(' ');
});

const regularPriceClass = computed(() => {
  const {
    regularPrice: regularPriceValue,
    discountPrice: discountPriceValue,
  } = friendtalkCommerce.value;
  const classList = [];
  const hover = position.value === 'commerce-regularPrice';

  if (regularPriceValue && !discountPriceValue) {
    classList.push('price__sale');
  } else {
    classList.push('price__basic');
  }

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  if (!friendtalkCommerce.value.regularPrice) {
    classList.push('default-value-display');
  }

  return classList.join(' ');
});

const discountRateClass = computed(() => {
  const classList = [];
  const hover = position.value === 'commerce-discountRate';
  const {
    regularPrice: regularPriceValue,
    discountPrice: discountPriceValue,
    discountRate: discountRateValue,
  } = friendtalkCommerce.value;

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  if (!discountRate.value) {
    classList.push('d-none');
  }

  if (regularPriceValue && !discountPriceValue && !discountRateValue) {
    classList.push('d-none');
  }

  if (!discountRateValue) {
    classList.push('default-value-display');
  }

  return classList.join(' ');
});

const discountFixedClass = computed(() => {
  const classList = [];
  const hover = position.value === 'commerce-discountFixed';

  if (discountRate.value) {
    classList.push('d-none');
  }

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  return classList.join(' ');
});

const buttons = computed(() => {
  const temp = friendtalkCommerce.value.buttons;

  if (temp?.length) {
    return temp;
  }

  return [{ name: t('placeholder.button') }];
});

const buttonsClass = computed(() => {
  const hover = position.value === 'commerce-buttons';

  return (hover && buttons.value) ? 'editAreaPositionHover' : '';
});

const couponClass = computed(() => {
  const list = [];
  const hover = position.value === 'commerce-coupon';

  if (hover) {
    list.push('editAreaPositionHover');
  }

  if (!friendtalkCommerce.value.coupon?.enabled) {
    list.push('d-none');
  }

  return list.join(' ');
});

const additionalContentClass = computed(() => {
  if (!additionalContent.value) {
    return 'd-none';
  }

  const hover = position.value === 'commerce-additionalContent';

  return hover ? 'editAreaPositionHover' : '';
});

const couponTitle = computed(() => {
  const titleString = friendtalkCommerce.value?.coupon?.name;
  const type = friendtalkCommerce.value?.coupon?.type;
  const str = t(couponType[type] ?? 'empty');
  const value = type === 'DELIVERY_DISCOUNT' ? str : `${titleString}${str.slice(2)}`;

  return value;
});

const couponDescription = computed(() => friendtalkCommerce.value?.coupon?.description || '');

defineOptions({
  inheritAttrs: false,
});

</script>
<template>
  <div class="preview-talk preview-talk--active">
    <div class="preview-inner mockup-scroll">
      <PreviewHeader
        :kakao-profile="kakaoProfile"
      />
      <div class="preview-message preview-message--active commerce">
        <div class="preview-message__container">
          <div class="preview-message__content">
            <div
              class="preview-message__inner-img"
              :class="imageClass"
            >
              <ImageComponent
                :src="imageUrl"
                alt="commerce-image"
              />
            </div>
            <div class="preview-message__inner">
              <div
                class="commerce__title"
                :class="titleClass"
              >
                {{ title }}
              </div>

              <div class="commerce__price">
                <div :class="discountPriceClass">
                  <span>{{ discountPrice }}</span>{{ discountPriceSuffix }}
                </div>
                <div :class="regularPriceClass">
                  <span>{{ regularPrice }}</span>{{ regularPriceSuffix }}
                </div>

                <div
                  class="price__percent"
                  :class="discountRateClass"
                >
                  <span>{{ discountRate }}</span><v-icon
                    class="custom__icon"
                    icon="mdi-percent-outline"
                    size="small"
                  />
                </div>
                <div
                  class="price__percent"
                  :class="discountFixedClass"
                >
                  <span>{{ discountFixed }}</span>{{ discountFixedSuffix }}<v-icon
                    class="custom__icon"
                    icon="mdi-arrow-down"
                    size="small"
                  />
                </div>

              </div>
              <div
                class="commerce__desc"
                :class="additionalContentClass"
              >
                {{ additionalContent }}
              </div>
            </div>
            <div
              class="preview-message__button"
              :class="buttonsClass"
            >
              <button
                v-for="(item, index) in buttons"
                :key="`kakao-preview-button${index}`"
                :class="{ 'preview-message__button--yellow': item.type === 'BF' }"
                type="button"
              >
                {{ item.name }}
              </button>
            </div>
            <div class="preview-message__coupon">
              <button
                type="button"
                :class="couponClass"
              >
                <div class="coupon__text">
                  <h5 class="coupon__title">{{ couponTitle }}</h5>
                  <p class="coupon__desc">{{ couponDescription }}</p>
                </div>
                <div class="coupon__icon">
                  <i></i>
                  <span>COUPON</span>
                </div>
              </button>
            </div>
          </div>
        </div>
        <PreviewFooter />
      </div>
    </div>
  </div>
</template>
<style scoped>
</style>
